import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import Navbar from "../../Components/Navbar"
import TimelineTab from "./TimelineTab"
import AnalyticsTab from "./AnalyticsTab"
import ReactJson from "react-json-view"

const getTurnByTurnHistory = (
  gameHistory: IMinifiedGameHistory
): IMinifiedGameHistory => {
  const historyByTurn: Record<number, IMinifiedHistoryItem> = {}
  const totalTurns = gameHistory.constantData.settings.maxTurns
  gameHistory.history.forEach((item) => {
    let currentTurn = totalTurns - item.situation.turnsLeft
    historyByTurn[currentTurn] = item
  })
  const turnByTurnHistory = Object.values(
    historyByTurn
  ) as IMinifiedHistoryItem[]
  const modifiedHistoryItems = gameHistory.history.map((item) => {
    return {
      ...item,
      situation: {
        ...item.situation,
        players: item.situation.players.map((player) => {
          return {
            ...player,
            pieceId: player.order.toString(),
          }
        }),
      },
    }
  })
  return { ...gameHistory, history: modifiedHistoryItems }
}
const GameHistoryDetail = () => {
  const { gameid } = useParams<{ gameid: string }>()
  const [gameHistory, setGameHistory] = useState<IMinifiedGameHistory | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<"timeline" | "analytics">(
    "timeline"
  )

  useEffect(() => {
    const fetchGameHistory = async () => {
      if (!gameid) {
        setError("Game ID is required")
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `https://history-proxy.maheerali121.workers.dev/${gameid}-history.json`
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch game history: ${response.status}`)
        }

        const data = await response.json()
        setGameHistory(getTurnByTurnHistory(data))
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load game history"
        )
      } finally {
        setLoading(false)
      }
    }

    fetchGameHistory()
  }, [gameid])

  if (loading) {
    return (
      <div
        className="w-screen h-screen bg-cover flex flex-col"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-white text-2xl">Loading game history...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="w-screen h-screen bg-cover flex flex-col"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-white text-2xl text-center">
            <div className="mb-4">Error loading game history</div>
            <div className="text-red-400">{error}</div>
          </div>
        </div>
      </div>
    )
  }

  if (!gameHistory) {
    return (
      <div
        className="w-screen h-screen bg-cover flex flex-col"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-white text-2xl">No game history found</div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="w-screen h-screen bg-cover flex flex-col"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <Navbar />
      <div className="flex-1 overflow-y-auto pt-6 pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] pb-[max(1.5rem,var(--safe-bottom))]">
        <div className="max-w-8xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-6 text-center">
            Game History - {gameHistory.gameid}
          </h1>

          {/* Tab Navigation */}
          <div className="bg-black/50 border border-gray-700 rounded-lg p-6 mb-6">
            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => setActiveTab("timeline")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === "timeline"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
              >
                Timeline
              </button>
              <button
                onClick={() => setActiveTab("analytics")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === "analytics"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
              >
                Analytics
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "timeline" && (
              <TimelineTab gameHistory={gameHistory} />
            )}
            {activeTab === "analytics" && (
              <AnalyticsTab gameHistory={gameHistory} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GameHistoryDetail
